/**
 * ProfileEditForm
 *
 * Lets a user change their profile. Modelled on the onboarding interview: the
 * same config-driven option lists and the SAME real org endpoints for role and
 * location (GET /roles, GET /locations via useOnboardingOrgOptions). Each change
 * is recorded as a new version server-side (PUT /me → user-record events).
 *
 * Role and unit are pickers over those endpoints and nothing else. A pick
 * writes an INTERNAL Reference ({reference:"Role/<id>", display}), which is
 * the only shape PUT /me accepts: a user's role is the row role_ref names and
 * the home unit is the row location_ref names, so there is no text a user
 * could type that either field could hold. Where the deployment offers no
 * list to pick from, the field shows what the user holds today, read-only.
 * Email is shown read-only too, because it is immutable.
 *
 * Resilience: while the org options load we show a spinner on just the
 * role/location pickers, and a genuine fetch failure is a visible error with
 * a retry rather than a field that quietly does nothing.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { useProfileFieldOptions } from '@/hooks/useProfileFieldOptions';
import { useOnboardingOrgOptions } from '@/hooks/useOnboardingOrgOptions';
import { useUpdateProfile } from '@/hooks/useProfile';
import { isInstitutionOrgDerived } from '@/components/profile/institutionOrigin';
import { FIELD_MAX_LENGTHS } from '@/lib/onboarding/validation';
import { SUPPORTED_LANGUAGES } from '@/utils/language';
import {
  CLEARED_REFERENCE,
  internalRef,
  localId,
  referenceLabel,
  referenceSameTarget,
  type Reference,
} from '@/lib/reference';
import type { UpdateProfileRequest, User } from '@/api/types';

interface ProfileEditFormProps {
  user: User;
  onDone: () => void;
}

/** A wrapped row of single-select chips. */
function ChipRow({
  options,
  selected,
  onSelect,
  disabled,
}: {
  options: { value: string; label: string }[];
  selected: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isSelected = opt.value === selected && selected !== '';
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            disabled={disabled}
            aria-pressed={isSelected}
            className={cn(
              'px-3 py-2 rounded-full text-sm transition-colors duration-150 disabled:opacity-50',
              isSelected
                ? 'bg-primary text-white border border-primary'
                : 'bg-white border border-neutral-200 hover:border-primary hover:bg-primary/5',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-500">{label}</label>
      {children}
    </div>
  );
}

/**
 * Visible error + retry for an org-backed picker whose fetch genuinely
 * failed. Deliberately not a free-text fallback: typing into a substitute
 * input here would silently write a display-only (non-ACL) Reference where
 * an internal one was expected.
 */
function OrgFieldError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-sm text-error">
      <span>{t('profile.errors.orgOptionsLoadFailed')}</span>
      <button
        type="button"
        onClick={onRetry}
        className="font-medium text-primary hover:text-primary-600 active:scale-95"
      >
        {t('actions.retry')}
      </button>
    </div>
  );
}

/** Visible "nothing configured yet" for an org-enabled picker with a real,
 *  successfully-fetched but empty list — distinct from a fetch failure. */
function OrgFieldEmpty() {
  const { t } = useTranslation();
  return <p className="text-sm text-neutral-400">{t('profile.errors.orgOptionsEmpty')}</p>;
}

/**
 * A value this form cannot change, rendered in the same input as the editable
 * fields so the column reads as one form. Email is immutable, and a role, a
 * unit or an org-derived institution is the row it points at, so there is no
 * text this form could accept for it.
 */
function ReadOnlyInput({ value }: { value: string }) {
  return (
    <Input
      value={value}
      disabled
      className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
    />
  );
}

/**
 * Role and unit: a picker over an org endpoint (GET /roles, GET /locations)
 * writing an internal Reference, which is the only value PUT /me accepts for
 * either field. Both fields answer the same five states, so they are decided
 * once here: still loading, the fetch failed, a list to pick from, no list but
 * a value the user holds today, and a list that is genuinely empty.
 */
function OrgPickerField({
  label,
  loading,
  failed,
  onRetry,
  options,
  selectedId,
  onPick,
  currentText,
  busy,
  children,
}: {
  label: string;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  options: { id: string; name: string }[];
  selectedId: string;
  onPick: (option: { id: string; name: string }) => void;
  currentText: string;
  busy: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <Spinner /> {t('common.loading')}
        </div>
      ) : failed ? (
        <OrgFieldError onRetry={onRetry} />
      ) : options.length > 0 ? (
        <ChipRow
          options={options.map((o) => ({ value: o.id, label: o.name }))}
          selected={selectedId}
          onSelect={(id) => {
            const picked = options.find((o) => o.id === id);
            if (picked) onPick(picked);
          }}
          disabled={busy}
        />
      ) : currentText ? (
        <ReadOnlyInput value={currentText} />
      ) : (
        <OrgFieldEmpty />
      )}
      {children}
    </Field>
  );
}

export function ProfileEditForm({ user, onDone }: ProfileEditFormProps) {
  const { t } = useTranslation();
  const config = useProfileFieldOptions();
  const orgOptionsQuery = useOnboardingOrgOptions();
  const updateProfile = useUpdateProfile();

  const org = orgOptionsQuery.data;
  const orgRoles = org?.enabled ? org.roles : [];
  const orgLocations = org?.enabled ? org.locations : [];
  // A role/location pick writes an internal, ACL-bound Reference, and that is
  // the only value PUT /me accepts for either field. A fetch FAILURE renders a
  // visible error and a retry; a deployment with self-assign off, and one
  // whose list is genuinely empty, render what the user holds today read-only.
  const orgRoleFailed = !!org?.enabled && org.rolesFailed;
  const orgLocationFailed = !!org?.enabled && org.locationsFailed;

  // Scalar form state, seeded from the current profile.
  const [name, setName] = useState(user.name ?? '');
  const [years, setYears] = useState<number | null>(user.yearsExperience);
  const [specialisation, setSpecialisation] = useState(user.specialisation ?? '');
  const [institution, setInstitution] = useState(user.institution ?? '');
  const [twinName, setTwinName] = useState(user.twinName);
  const [narrative, setNarrative] = useState(user.narrative ?? '');
  const [language, setLanguage] = useState<'de' | 'fr' | 'en'>(user.language);
  const [nameError, setNameError] = useState<string | null>(null);
  const [twinError, setTwinError] = useState<string | null>(null);

  // Reference state for the resource pointers.
  const [roleRef, setRoleRef] = useState<Reference | null>(user.roleRef ?? null);
  const [locationRef, setLocationRef] = useState<Reference | null>(user.locationRef ?? null);

  const roleText = referenceLabel(roleRef);
  const unitText = referenceLabel(locationRef);
  const selectedRoleId = localId(roleRef, 'Role') ?? '';
  const selectedLocationId = localId(locationRef, 'Location') ?? '';

  // Institution is read-only whenever the deployment resolves it from the org
  // tree — organisationRef present AND this user actually holds a location
  // membership somewhere in it (isInstitutionOrgDerived; hasOrgPlacement is
  // the same fact the "ward memberships" list below already renders from) —
  // OR the onboarding config itself marks the question non-editable
  // (auto-inserted / deployment-fixed). Without the membership check this
  // shadowed a real external user's own institution with the deployment's
  // org name (a placeholder org root, prod: "Starfleet Medical") purely
  // because SOME org root exists, regardless of whether THIS caller belongs
  // to it. String-only deployments with an editable question keep the
  // chips+freetext editor unchanged; a non-member user, even on an
  // org-backed deployment, is a string-only case FOR THEM.
  const hasOrgPlacement = !!user.locationMemberships?.length;
  const institutionOrgDerived = isInstitutionOrgDerived(user.organisationRef, hasOrgPlacement);
  const institutionReadOnly = institutionOrgDerived || !config.isFieldEditable('institution');
  const institutionDisplayValue = institutionOrgDerived
    ? referenceLabel(user.organisationRef) || institution
    : institution;

  const yearsOptions = useMemo(
    () => config.getYearsOptions().map((o) => ({ value: String(o.value), label: t(o.labelKey) })),
    [config, t],
  );
  const specialisationOptions = useMemo(
    () =>
      config.optionsFor('specialisation').map((key) => {
        const label = t(`onboarding.specialisations.${key}`, { defaultValue: key });
        return { value: label, label };
      }),
    [config, t],
  );
  const institutionOptions = useMemo(
    () =>
      config.optionsFor('institution').map((key) => {
        const label = t(`onboarding.institutions.${key}`, { defaultValue: key });
        return { value: label, label };
      }),
    [config, t],
  );
  const assistantNameOptions = useMemo(
    () => config.optionsFor('assistant_name').map((n) => ({ value: n, label: n })),
    [config],
  );

  /** Build a minimal patch: only fields the user changed. Free text is trimmed. */
  const buildPayload = (): UpdateProfileRequest => {
    const payload: UpdateProfileRequest = {};
    const trimmedName = name.trim();
    const trimmedSpec = specialisation.trim();
    const trimmedInstitution = institution.trim();
    const trimmedTwin = twinName.trim();

    if (trimmedName !== (user.name ?? '')) payload.name = trimmedName;
    if (trimmedSpec !== (user.specialisation ?? '')) payload.specialisation = trimmedSpec;
    if (trimmedInstitution !== (user.institution ?? '')) payload.institution = trimmedInstitution;
    if (trimmedTwin !== user.twinName) payload.twin_name = trimmedTwin;
    // Absent, null and a string are three different writes here — leave it
    // alone, clear it, replace it — and the backend keeps them apart
    // (optionalString). Collapsing an emptied box into an omitted field would
    // silently refuse to let her delete what she wrote.
    const trimmedNarrative = narrative.trim();
    if (trimmedNarrative !== (user.narrative ?? '')) {
      payload.narrative = trimmedNarrative === '' ? null : trimmedNarrative;
    }
    if (language !== user.language) payload.language = language;
    if (years !== null && years !== user.yearsExperience) payload.years_experience = years;
    // Identity-based change detection (referenceSameTarget): a same-target
    // re-pick with only a refreshed display label is NOT a change, so we don't
    // send it. Clearing (state → null) sends CLEARED_REFERENCE — the typed
    // sentinel the backend recognises via Reference.IsZero() (null on the wire
    // would read as "field omitted" = leave unchanged).
    if (!referenceSameTarget(roleRef, user.roleRef)) payload.role_ref = roleRef ?? CLEARED_REFERENCE;
    if (!referenceSameTarget(locationRef, user.locationRef)) payload.location_ref = locationRef ?? CLEARED_REFERENCE;
    return payload;
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(t('profile.validation.nameRequired'));
      return;
    }
    setNameError(null);
    // The assistant (twin) name is required — an empty value would break LLM
    // personalisation that addresses the user via it (mirrors the name guard).
    if (!twinName.trim()) {
      setTwinError(t('onboarding.validation.assistantNameRequired', { defaultValue: 'Assistant name is required' }));
      return;
    }
    setTwinError(null);
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      onDone();
      return;
    }
    updateProfile.mutate(payload, { onSuccess: () => onDone() });
  };

  const busy = updateProfile.isPending;

  return (
    <div className="space-y-6">
      {/* Email — immutable */}
      <Field label={t('auth.email')}>
        <ReadOnlyInput value={user.email} />
      </Field>

      {/* Name */}
      <Field label={t('profile.name')}>
        <Input
          value={name}
          maxLength={FIELD_MAX_LENGTHS.name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          className="h-12 text-base"
          placeholder={t('profile.name')}
        />
        {nameError && <p className="text-sm text-error">{nameError}</p>}
      </Field>

      {/* Role. A picker over GET /roles, writing an internal Reference. With
          no list to pick from, the role the user holds today is shown
          read-only: there is no text this field could accept. */}
      <OrgPickerField
        label={t('profile.role')}
        loading={orgOptionsQuery.isLoading}
        failed={orgRoleFailed}
        onRetry={orgOptionsQuery.retryRoles}
        options={orgRoles}
        selectedId={selectedRoleId}
        onPick={(role) => setRoleRef(internalRef('Role', role.id, role.name))}
        currentText={roleText}
        busy={busy}
      />

      {/* Years of experience */}
      <Field label={t('profile.yearsExperience')}>
        <ChipRow
          options={yearsOptions}
          selected={years !== null ? String(years) : ''}
          onSelect={(v) => setYears(Number(v))}
          disabled={busy}
        />
      </Field>

      {/* Specialisation */}
      <Field label={t('profile.specialisation')}>
        <ChipRow options={specialisationOptions} selected={specialisation} onSelect={setSpecialisation} disabled={busy} />
        <Input
          value={specialisation}
          maxLength={FIELD_MAX_LENGTHS.specialisation}
          onChange={(e) => setSpecialisation(e.target.value)}
          disabled={busy}
          className="h-12 text-base mt-2"
          placeholder={t('onboarding.inputPlaceholder')}
        />
      </Field>

      {/* Narrative — her own summary of her practice, in her own words.
          It replaces the AI-written profile summary adr/0025 removed: no
          service generates it, no other route writes it, and clearing the box
          clears the attribute rather than restoring a generated paragraph.
          No maxLength: the backend imposes none, and a cap invented here would
          truncate a practitioner mid-sentence for no stated reason. */}
      <Field label={t('profile.narrative')}>
        <Textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          disabled={busy}
          rows={4}
          className="text-base"
          placeholder={t('profile.narrativePlaceholder')}
        />
      </Field>

      {/* Institution — read-only display (like email) when org-derived or the
          onboarding config marks it non-editable; otherwise the usual
          chips+freetext editor for string-only deployments. A non-member
          user with no stored value must show as genuinely absent (the
          institutionEmpty placeholder, same convention as unitEmpty below),
          never a fabricated org name and never a blank box that could read
          as a loading glitch. */}
      <Field label={t('profile.institution')}>
        {institutionReadOnly ? (
          <ReadOnlyInput value={institutionDisplayValue || t('profile.institutionEmpty')} />
        ) : (
          <>
            <ChipRow options={institutionOptions} selected={institution} onSelect={setInstitution} disabled={busy} />
            <Input
              value={institution}
              maxLength={FIELD_MAX_LENGTHS.institution}
              onChange={(e) => setInstitution(e.target.value)}
              disabled={busy}
              className="h-12 text-base mt-2"
              placeholder={t('onboarding.inputPlaceholder')}
            />
          </>
        )}
      </Field>

      {/* Unit. A picker over GET /locations, writing an internal Reference.
          With no list to pick from, the unit the user holds today is shown
          read-only, on the same rule as the role field above. */}
      <OrgPickerField
        label={t('profile.unit')}
        loading={orgOptionsQuery.isLoading}
        failed={orgLocationFailed}
        onRetry={orgOptionsQuery.retryLocations}
        options={orgLocations}
        selectedId={selectedLocationId}
        onPick={(location) => setLocationRef(internalRef('Location', location.id, location.name))}
        currentText={unitText}
        busy={busy}
      >
        {/* Admin-granted ward memberships — FGA grants the user cannot edit
            here, listed read-only alongside the self-assign picker above.
            locationMemberships is optional on the wire (an older backend or a
            response still in flight); absence renders nothing extra, same as
            an empty resolved list. */}
        {!!user.locationMemberships?.length && (
          <div className="mt-2 space-y-1">
            <p className="text-xs text-neutral-400">{t('profile.wardMemberships')}</p>
            <div className="flex flex-wrap gap-2">
              {user.locationMemberships.map((m) => (
                <span
                  key={m.location_id}
                  className="px-3 py-2 rounded-full text-sm bg-neutral-100 border border-neutral-200 text-neutral-600"
                >
                  {m.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </OrgPickerField>

      {/* Assistant (twin) name */}
      <Field label={t('profile.twinName')}>
        <ChipRow options={assistantNameOptions} selected={twinName} onSelect={setTwinName} disabled={busy} />
        <Input
          value={twinName}
          maxLength={FIELD_MAX_LENGTHS.assistant_name}
          onChange={(e) => setTwinName(e.target.value)}
          disabled={busy}
          className="h-12 text-base mt-2"
          placeholder={t('onboarding.inputPlaceholder')}
        />
        {twinError && <p className="text-sm text-error">{twinError}</p>}
      </Field>

      {/* Language */}
      <Field label={t('profile.language')}>
        <ChipRow
          options={SUPPORTED_LANGUAGES.map((l) => ({ value: l, label: t(`onboarding.languages.${l}`) }))}
          selected={language}
          onSelect={(v) => setLanguage(v as 'de' | 'fr' | 'en')}
          disabled={busy}
        />
      </Field>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onDone} disabled={busy} className="flex-1 h-12">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={busy} className="flex-1 h-12">
          {busy ? t('common.saving') : t('profile.save')}
        </Button>
      </div>
    </div>
  );
}
